<template>
  <div class="filter-panel" :class="{ compact }">
    <div class="filter-grid">
      <template v-for="filter in filters">
      <div
        v-if="!filter.hidden"
        :key="filter.id"
        class="filter-item"
      >
        <!-- Static select for Place Type -->
        <template v-if="filter.type === 'static'">
          <label class="filter-label">{{ filter.name }}</label>
          <div class="filter-input-wrapper">
            <b-form-select
              :value="activeFilters[filter.param] || ''"
              :options="sortedPlaceTypeOptions"
              :disabled="isError(filter.param)"
              @input="onStaticSelect(filter.param, $event)"
            />
            <button
              v-if="activeFilters[filter.param] && !isRequired(filter)"
              class="clear-btn"
              type="button"
              aria-label="Clear filter"
              @click="onClear(filter.param)"
            >
              &times;
            </button>
          </div>
        </template>

        <!-- Typeahead dropdown for other filters -->
        <template v-else>
          <label class="filter-label">{{ filter.name }}</label>
          <div class="filter-input-wrapper">
            <b-dropdown
              class="filter-dropdown"
              variant="outline-secondary"
              block
              menu-class="w-100"
              :disabled="isError(filter.param)"
              @shown="onDropdownShown(filter)"
            >
              <template #button-content>
                <span v-if="isError(filter.param)" class="placeholder-text">
                  Options unavailable
                </span>
                <span v-else-if="activeFilters[filter.param]" class="selected-value">
                  {{ activeFilterLabels[filter.param] || activeFilters[filter.param] }}
                </span>
                <span v-else class="placeholder-text">
                  {{ filter.name }}
                </span>
              </template>

              <b-dropdown-form @submit.stop.prevent="() => {}">
                <b-form-input
                  v-model="searchTerms[filter.param]"
                  type="search"
                  autocomplete="off"
                  :placeholder="'Search ' + filter.name + '...'"
                  :ref="'input-' + filter.param"
                  @input="onTypeaheadInput(filter.param, $event)"
                />
              </b-dropdown-form>

              <b-dropdown-divider />

              <!-- Loading state -->
              <b-dropdown-text v-if="isLoading(filter.param)" class="text-center">
                <b-spinner small variant="primary" />
                <span class="ml-2">Loading...</span>
              </b-dropdown-text>

              <!-- Suggestions list (sorted alphabetically, case-insensitive) -->
              <template v-else-if="getSuggestions(filter.param).length > 0">
                <b-dropdown-item-button
                  v-for="option in getSuggestions(filter.param)"
                  :key="option.id"
                  @click="onSuggestionSelect(filter.param, option)"
                >
                  {{ option.name }}
                </b-dropdown-item-button>
              </template>

              <!-- Empty state (not loading, no suggestions, user has typed) -->
              <b-dropdown-text
                v-else-if="searchTerms[filter.param] && !isLoading(filter.param)"
                class="text-muted"
              >
                No results found
              </b-dropdown-text>

              <!-- Type-to-search prompt: completion-backed filters (State/City)
                   return no options for an empty prefix, so prompt the user to
                   type instead of showing a misleadingly empty list. -->
              <b-dropdown-text
                v-else-if="isTypeToSearch(filter) && !searchTerms[filter.param] && !isLoading(filter.param)"
                class="text-muted"
              >
                Type to search...
              </b-dropdown-text>
            </b-dropdown>

            <button
              v-if="activeFilters[filter.param] && !isRequired(filter)"
              class="clear-btn"
              type="button"
              aria-label="Clear filter"
              @click="onClear(filter.param)"
            >
              &times;
            </button>
          </div>
        </template>
      </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "FilterPanel",
  props: {
    filters: {
      type: Array,
      default: () => [],
    },
    activeFilters: {
      type: Object,
      default: () => ({}),
    },
    activeFilterLabels: {
      type: Object,
      default: () => ({}),
    },
    suggestions: {
      type: Object,
      default: () => ({}),
    },
    loadingFilters: {
      type: Object,
      default: () => ({}),
    },
    errorFilters: {
      type: Object,
      default: () => ({}),
    },
    // When true, collapse the filter grid to a single column regardless of
    // viewport width, for use inside narrow containers (e.g. the Select tab).
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchTerms: {},
      debounceTimers: {},
      placeTypeOptions: [
        { value: "", text: "Place Type" },
        { value: "Polygons", text: "Polygon" },
        { value: "POI", text: "Point" },
      ],
    };
  },
  computed: {
    // Place Type options with the empty placeholder pinned first and the real
    // options sorted alphabetically (case-insensitive) by their display text.
    sortedPlaceTypeOptions() {
      const placeholders = this.placeTypeOptions.filter((o) => o.value === "");
      const options = this.placeTypeOptions
        .filter((o) => o.value !== "")
        .slice()
        .sort((a, b) => this.compareByName(a.text, b.text));
      return [...placeholders, ...options];
    },
  },
  methods: {
    isLoading(param) {
      return !!this.loadingFilters[param];
    },
    isError(param) {
      return !!this.errorFilters[param];
    },
    // Compare two display strings case-insensitively using a locale-aware
    // compare so mixed-case and accented names order naturally (e.g. "Alto",
    // "beta", "Delta"). Null/undefined/non-string values are treated as empty
    // strings so sorting never throws.
    compareByName(a, b) {
      const left = typeof a === "string" ? a : "";
      const right = typeof b === "string" ? b : "";
      return left.localeCompare(right, undefined, { sensitivity: "base" });
    },
    getSuggestions(param) {
      // Return a sorted copy so the incoming `suggestions` prop is never mutated;
      // items are ordered alphabetically (case-insensitive) by display name.
      return [...(this.suggestions[param] || [])].sort((a, b) =>
        this.compareByName(a.name, b.name)
      );
    },
    onDropdownShown(filter) {
      this.$nextTick(() => {
        const ref = this.$refs['input-' + filter.param];
        if (ref && ref[0]) {
          ref[0].$el.focus();
        } else if (ref && ref.focus) {
          ref.focus();
        }
      });
      // Completion-backed filters (State/City) return no options for an empty
      // prefix, so firing an empty suggest on open just yields an empty list.
      // Skip the auto-populate for those; the "Type to search..." prompt guides
      // the user instead. Typing still triggers a suggest via onTypeaheadInput.
      if (this.isTypeToSearch(filter)) {
        return;
      }
      // Auto-populate an initial set of options when the dropdown opens,
      // as long as the user hasn't typed a search term and nothing is loaded yet.
      if (
        !this.searchTerms[filter.param] &&
        !this.isLoading(filter.param) &&
        this.getSuggestions(filter.param).length === 0
      ) {
        this.$emit("suggest", filter.param, "");
      }
    },
    // A filter whose backing suggestion source is an OpenSearch completion
    // suggester, which requires a non-empty prefix and therefore cannot return
    // an initial list on open. State (admin_area_lv1) and City (admin_area_lv3)
    // are type-to-search only.
    isTypeToSearch(filter) {
      return !!filter && (filter.param === "state" || filter.param === "city");
    },
    onTypeaheadInput(param, value) {
      if (this.debounceTimers[param]) {
        clearTimeout(this.debounceTimers[param]);
      }
      this.debounceTimers[param] = setTimeout(() => {
        this.$emit("suggest", param, value);
      }, 300);
    },
    onSuggestionSelect(param, option) {
      this.searchTerms[param] = "";
      this.$emit("select", param, option);
    },
    onStaticSelect(param, value) {
      if (value === "") {
        this.$emit("clear", param);
      } else {
        this.$emit("select", param, { id: value, name: value });
      }
    },
    onClear(param) {
      this.searchTerms[param] = "";
      this.$emit("clear", param);
    },
    isRequired(filter) {
      // Default to treating a filter as required (hide the ×) when its required
      // status cannot be determined, so we never expose a clear path we're
      // unsure about (R5.5). A filter is shown as clearable only when it is
      // explicitly NOT required.
      if (!filter || typeof filter !== "object") return true; // indeterminate → hide ×
      return filter.required === true;
    },
  },
  beforeDestroy() {
    Object.values(this.debounceTimers).forEach((timer) => {
      if (timer) clearTimeout(timer);
    });
  },
};
</script>

<style lang="scss" scoped>
.filter-panel {
  padding: 1.25rem 0;
}

.filter-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, 1fr);
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.filter-label {
  color: #12254b;
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0;
}

.filter-input-wrapper {
  position: relative;

  .clear-btn {
    align-items: center;
    background: none;
    border: none;
    color: #6e84a3;
    cursor: pointer;
    display: flex;
    font-size: 1.2rem;
    height: 100%;
    justify-content: center;
    padding: 0 0.5rem;
    position: absolute;
    right: 2rem;
    top: 0;

    &:hover {
      color: #12254b;
    }
  }
}

.filter-dropdown {
  width: 100%;

  ::v-deep .dropdown-toggle {
    border-color: #d8e2ef;
    border-radius: 0.6rem;
    color: #12254b;
    min-height: 38px;
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;

    // Keep the toggle light on hover/focus/active instead of letting
    // BootstrapVue's outline-secondary variant fill it with the dark
    // secondary color. Matches the site-wide convention (see NearByPlaces.vue).
    &:hover,
    &:focus,
    &:active,
    &.active {
      background-color: #fff !important;
      border-color: #d8e2ef !important;
      box-shadow: none !important;
      color: #12254b !important;
    }
  }

  // Preserve the light look while the dropdown menu is open (BootstrapVue
  // adds `.show` to the wrapper and applies the active-button styling).
  ::v-deep.show > .dropdown-toggle {
    background-color: #fff !important;
    border-color: #d8e2ef !important;
    box-shadow: none !important;
    color: #12254b !important;
  }

  ::v-deep .dropdown-menu {
    max-height: 250px;
    overflow-y: auto;
    width: 100% !important;
  }
}

.placeholder-text {
  color: #95aac9;
}

.selected-value {
  color: #12254b;
}

::v-deep .filter-input-wrapper > select {
  border-color: #d8e2ef;
  border-radius: 0.6rem;
  min-height: 38px;
}

@media (max-width: 992px) {
  .filter-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
}

// Compact layout for narrow containers: one filter per row regardless of
// viewport width.
.filter-panel.compact {
  padding: 0.75rem 0;

  .filter-grid {
    grid-template-columns: 1fr;
  }
}
</style>
